<template>
  <div class="modal-body">
    <div class="register-form p-4">
      <!-- Success Message -->
      <div v-if="successMessage" class="alert alert-success text-center">
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="alert alert-danger text-center">
        {{ errorMessage }}
      </div>
      <!-- Step Progress Bar -->
      <div class="mb-4">
        <div class="progress" style="height: 6px">
          <div
            class="progress-bar bg-pink"
            role="progressbar"
            :style="{ width: step * 33.33 + '%' }"
          ></div>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <span :class="{ 'fw-bold text-pink': step === 1 }"
            ><i class="bi bi-person"></i> Personal Info</span
          >
          <span :class="{ 'fw-bold text-pink': step === 2 }"
            ><i class="bi bi-envelope"></i> Account Info</span
          >
          <span :class="{ 'fw-bold text-pink': step === 3 }"
            ><i class="bi bi-journal-bookmark"></i> Enrollment</span
          >
        </div>
      </div>

      <form @submit.prevent="nextStep">
        <!-- Step 1: Personal Info -->
        <div v-if="step === 1">
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-person-fill"></i> First Name</label
            >
            <input
              type="text"
              class="form-control"
              v-model="newStudent.first_name"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-person-fill"></i> Last Name</label
            >
            <input
              type="text"
              class="form-control"
              v-model="newStudent.last_name"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-telephone-fill"></i> Phone</label
            >
            <input
              type="text"
              class="form-control"
              v-model="newStudent.phone"
              required
            />
          </div>

          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-pink"
              :disabled="!stepValid"
              @click="nextStep"
            >
              Next <i class="bi bi-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Step 2: Account Info -->
        <div v-if="step === 2">
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-envelope-fill"></i> Email</label
            >
            <input
              type="email"
              class="form-control"
              v-model="newStudent.email"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-lock-fill"></i> Password</label
            >
            <div class="input-group">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                v-model="newStudent.password"
                required
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-lock-fill"></i> Confirm Password</label
            >
            <div class="input-group">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                v-model="confirmPassword"
                required
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i
                  :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
                ></i>
              </button>
            </div>
            <small v-if="passwordMismatch" class="text-danger"
              >Passwords do not match</small
            >
          </div>

          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-secondary" @click="step = 1">
              <i class="bi bi-arrow-left"></i> Back
            </button>
            <button
              type="button"
              class="btn btn-pink"
              :disabled="!stepValid"
              @click="nextStep"
            >
              Next <i class="bi bi-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Step 3: Enrollment Info -->
        <div v-if="step === 3">
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-mortarboard-fill"></i> Grade</label
            >
            <select
              class="form-select"
              v-model="newStudent.grade_id"
              @change="fetchSubjectsByGrade"
              required
            >
              <option value="" disabled>Select Grade</option>
              <option v-for="grade in grades" :key="grade.id" :value="grade.id">
                {{ grade.level }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-book-half"></i> Subject (Optional)</label
            >
            <select class="form-select" v-model="newStudent.subject_id">
              <option value="" selected>None</option>
              <option
                v-for="subject in subjects"
                :key="subject.id"
                :value="subject.id"
              >
                {{ subject.name }}
              </option>
            </select>
          </div>

          <!-- <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-calendar-event"></i> Valid Until</label
            >
            <input
              type="date"
              class="form-control"
              v-model="newStudent.valid_until"
              required
            />
          </div> -->

          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-check-circle-fill"></i> Enrollment Status</label
            >
            <select
              class="form-select"
              v-model="newStudent.enrollment_status"
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-secondary" @click="step = 2">
              <i class="bi bi-arrow-left"></i> Back
            </button>
            <button
              type="submit"
              class="btn btn-pink"
              :disabled="!stepValid || loading"
            >
              <i class="bi bi-save"></i> {{ loading ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RegisterModel",
  data() {
    return {
      step: 1,
      newStudent: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        password: "",
        grade_id: "",
        subject_id: "",
        valid_until: "",
        enrollment_status: "active",
      },
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      successMessage: "",
      errorMessage: "",
      grades: [],
      subjects: [],
    };
  },
  computed: {
    passwordMismatch() {
      return (
        this.confirmPassword &&
        this.newStudent.password !== this.confirmPassword
      );
    },
    stepValid() {
      if (this.step === 1)
        return (
          this.newStudent.first_name &&
          this.newStudent.last_name &&
          this.newStudent.phone
        );
      if (this.step === 2)
        return (
          this.newStudent.email &&
          this.newStudent.password &&
          this.confirmPassword &&
          !this.passwordMismatch
        );
      if (this.step === 3)
        return (
          this.newStudent.grade_id &&
          // this.newStudent.valid_until &&
          this.newStudent.subject_id &&
          this.newStudent.enrollment_status
        );
      return false;
    },
  },
  mounted() {
    axios.get("/a-get-grades").then((res) => (this.grades = res.data));
    // axios.get("/a-get-subjects").then((res) => (this.subjects = res.data));
  },
  methods: {
    nextStep() {
      if (!this.stepValid) return;
      if (this.step < 3) this.step++;
      else this.registerStudent();
    },

    fetchSubjectsByGrade() {
      this.newStudent.subject_id = ""; // reset previous subject selection
      if (!this.newStudent.grade_id) {
        this.subjects = [];
        return;
      }

      axios
        .get(`/a-grade-subjects?grade_id=${this.newStudent.grade_id}`)
        .then((res) => {
          this.subjects = res.data.subjects; // set subjects dropdown
        })
        .catch((err) => {
          console.error(err);
          this.subjects = [];
        });
    },
    async registerStudent() {
      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";
      try {
        const res = await axios.post("/student-registration", this.newStudent);
        this.successMessage = "Student registered successfully!";
        this.$emit("student-added", res.data);

        // Hide message after 5 seconds
        setTimeout(() => (this.successMessage = ""), 5000);

        // Reset form after a short delay
        setTimeout(this.resetForm, 500);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          this.errorMessage = err.response.data.message;
        } else {
          this.errorMessage = "Something went wrong!";
        }

        // Hide error message after 5 seconds
        setTimeout(() => (this.errorMessage = ""), 5000);

        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.step = 1;
      this.newStudent = {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        password: "",
        grade_id: "",
        subject_id: "",
        // valid_until: "",
        enrollment_status: "active",
      };
      this.confirmPassword = "";
      this.showPassword = false;
      this.showConfirmPassword = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}
</style>
